import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import {useState} from "react";

import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import type {ImageInput, ImageSource} from "@khan/guide-types";

import {ImageAttachModalWrapper} from "../image-attach/image-attach-modal-wrapper.tsx";
import {
    InputModeMenu,
    type ImageInputCustomOptions,
} from "../image-attach/input-mode-menu.tsx";
import {useUserMayInputImages} from "../image-attach/use-user-may-input-images.ts";

import paperclip from "@phosphor-icons/core/regular/paperclip.svg";

interface LauncherProps {
    onAttachImages: (images: ImageInput[]) => void;
    customOpener?: () => React.ReactElement;
    tooltipContent?: string;
    dropdownStyle?: React.CSSProperties;
    customOptions?: ImageInputCustomOptions;
}

const AttachButton = ({
    disabled,
    openModal,
}: {
    disabled?: boolean;
    openModal?: () => void;
}) => (
    <IconButton
        disabled={disabled}
        icon={paperclip}
        aria-label={t`Attach image`}
        size="small"
        kind="tertiary"
        actionType="neutral"
        onClick={openModal}
        testId="image-attach-button"
    />
);

/**
 * A button that launches the image input modal.
 */
export function ImageInputModalLauncher({
    onAttachImages,
    customOpener,
    tooltipContent,
    dropdownStyle,
    customOptions,
}: LauncherProps) {
    const userMayInputImages = useUserMayInputImages();
    const imageInputMenuFlag = useFeatureIsOn("image-input-menu");
    const [imageSource, setImageSource] = useState<ImageSource | null>(null);

    if (!userMayInputImages) {
        return null;
    }

    const finalTooltipContent = tooltipContent || t`Attach image`;

    // New experience, currently behind a flag.
    // TODO: (KORE-1313) Clean this up when the image input menu is fully launched.
    if (imageInputMenuFlag) {
        if (!imageSource) {
            return (
                <Tooltip content={finalTooltipContent}>
                    <InputModeMenu
                        onOpenModal={setImageSource}
                        opener={customOpener}
                        dropdownStyle={dropdownStyle}
                        customOptions={customOptions}
                    />
                </Tooltip>
            );
        }

        return (
            <ModalLauncher
                modal={({closeModal}) => (
                    <ImageAttachModalWrapper
                        onCloseModal={() => {
                            setImageSource(null);
                            closeModal();
                        }}
                        onAttachImages={onAttachImages}
                        imageSource={imageSource}
                    />
                )}
                backdropDismissEnabled={true}
                opened={true}
                onClose={() => setImageSource(null)}
            />
        );
    }

    // Old experience, shown without flag.
    // TODO: (KORE-1313) Remove this when the image input menu is fully launched.
    return (
        <ModalLauncher
            modal={({closeModal}) => (
                <ImageAttachModalWrapper
                    onCloseModal={closeModal}
                    onAttachImages={onAttachImages}
                />
            )}
            backdropDismissEnabled={true}
        >
            {({openModal}) => (
                <Tooltip content={finalTooltipContent}>
                    {customOpener ? (
                        customOpener()
                    ) : (
                        <AttachButton openModal={openModal} />
                    )}
                </Tooltip>
            )}
        </ModalLauncher>
    );
}
